import { externalConstraint } from '@flatfile/plugin-constraints'
import { recordHook } from '@flatfile/plugin-record-hook'
import {
  dobFormattingListener,
  dobValidationListener,
  dodFormattingListener,
  dodValidationListener,
  ssnValidationListener,
  stateFormattingListener,
  zipCodeValidationListener,
} from './searchParticipantsDataFormatting'
import {
  ssnFormattingListener,
  zipCodeFormattingListener,
  planSponsorFormattingListener,
  planNameFormattingListener,
  planEinFormattingListener,
  planNumberFormattingListener,
} from './participantsDataFormatting'

export const searchServiceListeners = (listener) => {
  /*Data Formatting */

  //Plan Sponsor Formatting
  planSponsorFormattingListener(listener, 'search-record')

  //Plan Name Formattingq
  planNameFormattingListener(listener, 'search-record')
  //Plan EIN Formatting
  planEinFormattingListener(listener, 'search-record')

  //Plan Number Formatting
  planNumberFormattingListener(listener, 'search-record')

  //SSN Formatting and Validation
  ssnFormattingListener(listener, 'search-record')
  ssnValidationListener(listener, 'search-record')

  //State Formatting
  stateFormattingListener(listener, 'search-record')

  //Zip Formatting and Validation
  zipCodeFormattingListener(listener, 'search-record')
  zipCodeValidationListener(listener, 'search-record')

  //DOB Formatting and Validation
  dobFormattingListener(listener, 'search-record')
  dobValidationListener(listener, 'search-record')

  //DOD Formatting and Validation
  dodFormattingListener(listener, 'search-record')
  dodValidationListener(listener, 'search-record')

  /*Constraints*/

  listener.use(
    externalConstraint('planSponsorName', (value, key, { config, record }) => {
      if (
        value &&
        new RegExp(config.prohibitedCompanies.regex, 'i').test(value)
      ) {
        record.addError(key, config.prohibitedCompanies.error)
      }
      if (
        value &&
        new RegExp(config.createEmptyValueValidator.regex).test(value)
      ) {
        record.addError(key, config.createEmptyValueValidator.error)
      }
    })
  )

  listener.use(
    externalConstraint('planName', (value, key, { config, record }) => {
      if (
        value &&
        new RegExp(config.createEmptyValueValidator.regex).test(value)
      ) {
        record.addError(key, config.createEmptyValueValidator.error)
      }
    })
  )

  listener.use(
    externalConstraint('planEin', (value, key, { config, record }) => {
      if (value && !new RegExp(config.invalidEINFormat.regex).test(value)) {
        record.addError(key, config.invalidEINFormat.error)
      }
      if (value && !new RegExp(config.invalidEIN.regex).test(value)) {
        record.addError(key, config.invalidEIN.error)
      }
    })
  )

  listener.use(
    externalConstraint('planNumber', (value, key, { config, record }) => {
      if (value && new RegExp(config.invalidCharacters.regex).test(value)) {
        record.addError(key, config.invalidCharacters.error)
      }
      if (value && !new RegExp(config.mustBeThreeDigits.regex).test(value)) {
        record.addError(key, config.mustBeThreeDigits.error)
      }
    })
  )

  listener.use(
    externalConstraint('ssn', (value, key, { config, record }) => {
      if (value && new RegExp(config.ssnValidityCheck.regex).test(value)) {
        record.addError(key, config.ssnValidityCheck.error)
      }
      if (value && !new RegExp(config.formatCheck.regex).test(value)) {
        record.addError(key, config.formatCheck.error)
      }
    })
  )

  listener.use(
    recordHook('search-record', (record) => {
      // Define the address fields and SSN field
      const addressFields = ['address1', 'city', 'state', 'zipCode']
      const ssnField = 'ssn'

      // Normalize the address field values by removing empty strings
      const recordData = addressFields.reduce((acc, field) => {
        const value = record.get(field)
        acc[field] = value?.trim() === '' ? null : value
        return acc
      }, {})

      // Normalize the SSN field value by removing empty strings
      const ssnValue = record.get(ssnField)
      recordData[ssnField] = ssnValue?.trim() === '' ? null : ssnValue

      // Check if the record has a full address
      const hasFullAddress = addressFields.every(
        (field) => recordData[field] !== null
      )

      // Check if the record has an SSN
      const hasSsn = recordData[ssnField] !== null

      // If the record does not have a full address and no SSN, add an error
      if (!hasFullAddress && !hasSsn) {
        record.addError(ssnField, 'If no Address, required')
      }

      // If the record does not have an SSN, add errors to the address fields
      if (!hasSsn) {
        addressFields.forEach((field) => {
          // If the field is empty or has a value of 'N/A', add an error
          if (
            recordData[field] === null ||
            recordData[field].toLowerCase() === 'n/a'
          ) {
            record.addError(field, 'If no SSN, required')
          }
        })
      }
      return record
    })
  )

  listener.use(
    externalConstraint('firstName', (value, key, { config, record }) => {
      if (value && new RegExp(config.checkNamePrefix.regex, 'i').test(value)) {
        record.addError(key, config.checkNamePrefix.error)
      }
      if (
        value &&
        new RegExp(config.checkNotApplicable.regex, 'i').test(value)
      ) {
        record.addError(key, config.checkNotApplicable.error)
      }
      if (
        value &&
        new RegExp(config.createMaxCharValidator.regex).test(value)
      ) {
        record.addError(key, config.createMaxCharValidator.error)
      }
      if (
        value &&
        new RegExp(config.createEmptyValueValidator.regex).test(value)
      ) {
        record.addError(key, config.createEmptyValueValidator.error)
      }
    })
  )

  listener.use(
    externalConstraint('middleName', (value, key, { config, record }) => {
      if (
        value &&
        new RegExp(config.createMaxCharValidator.regex).test(value)
      ) {
        record.addError(key, config.createMaxCharValidator.error)
      }
    })
  )

  listener.use(
    externalConstraint('lastName', (value, key, { config, record }) => {
      if (
        value &&
        new RegExp(config.checkNotApplicable.regex, 'i').test(value)
      ) {
        record.addError(key, config.checkNotApplicable.error)
      }
      if (
        value &&
        new RegExp(config.createMaxCharValidator.regex).test(value)
      ) {
        record.addError(key, config.createMaxCharValidator.error)
      }
      if (
        value &&
        new RegExp(config.createEmptyValueValidator.regex).test(value)
      ) {
        record.addError(key, config.createEmptyValueValidator.error)
      }
    })
  )

  listener.use(
    externalConstraint('phone', (value, key, { config, record }) => {
      if (value && !new RegExp(config.phoneNumber.regex).test(value)) {
        record.addError(key, config.phoneNumber.error)
      }
    })
  )
}
